import React, { useState } from 'react';
import AIChatPopup from './AIChatPopup';
import { FEDERATION_PERSONAS } from '../data/federationPersonas';

/**
 * Federation Faces — Monolith Tile Row
 * -------------------------------------
 * Architect directive (2026-05-23): A shared row of 7 Federation persona
 * tiles, persistent on every page. Each tile triggers AIChatPopup with
 * the corresponding persona pre-selected. NO navigation — chat opens
 * in-place, fully voice-enabled (TTS + STT inherited from AIChatPopup).
 *
 * Layout: fixed left-edge vertical strip of monolith tiles (tall narrow
 * rectangles, 2001 obelisk silhouette). Scrolls vertically if viewport
 * is short. Positioned to stay clear of the header (z=10000) and the
 * EchoCore thin-bar (z=50).
 */
const TILE_W = 44;
const TILE_H = 60;

function MonolithTile({ persona, onSelect, dimmed }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const initial = persona.name.charAt(0).toUpperCase();
  const isExternal = Boolean(persona.externalOnly);
  const handleClick = () => {
    if (isExternal && persona.siteUrl) {
      window.open(persona.siteUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    onSelect(persona.id);
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`federation-face-${persona.id}`}
      data-persona={persona.id}
      data-external={isExternal ? 'true' : 'false'}
      aria-label={`${isExternal ? 'Open' : 'Chat with'} ${persona.name}, ${persona.role}`}
      title={`${persona.name} — ${persona.role}${isExternal ? ' (opens external site)' : ''}`}
      style={{
        width: `${TILE_W}px`,
        height: `${TILE_H}px`,
        padding: 0,
        background: '#0a0a0a',
        border: `2px solid ${persona.accent}`,
        borderRadius: '6px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: persona.current
          ? `0 0 14px ${persona.accent}88, inset 0 0 0 1px rgba(255,255,255,0.12)`
          : `0 0 6px ${persona.accent}33`,
        opacity: dimmed ? 0.5 : 1,
        transition: 'transform 180ms ease, box-shadow 180ms ease, opacity 180ms ease',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(4px) scale(1.05)';
        e.currentTarget.style.boxShadow = `0 0 22px ${persona.accent}cc`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0) scale(1)';
        e.currentTarget.style.boxShadow = persona.current
          ? `0 0 14px ${persona.accent}88, inset 0 0 0 1px rgba(255,255,255,0.12)`
          : `0 0 6px ${persona.accent}33`;
      }}
    >
      {!imgFailed ? (
        <img
          src={persona.avatar}
          alt=""
          onError={() => setImgFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: '20px',
            fontWeight: 700,
            color: persona.accent,
            background: 'linear-gradient(180deg, #0a0a0a 0%, #050b14 100%)',
          }}
        >
          {initial}
        </span>
      )}
      {/* Name label slides up on hover */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '2px 4px',
          background: `linear-gradient(0deg, ${persona.accent}dd 0%, transparent 100%)`,
          color: '#000',
          fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: '8px',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textAlign: 'center',
          textTransform: 'uppercase',
          lineHeight: 1.2,
        }}
      >
        {persona.name}
      </span>
      {persona.current && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: persona.accent,
            boxShadow: `0 0 6px ${persona.accent}`,
          }}
        />
      )}
    </button>
  );
}

export default function FederationFaces() {
  const [activePersona, setActivePersona] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Treat anything narrower than 768px (Tailwind md breakpoint) as mobile.
  // The bar is collapsed by default there so it stops covering page content
  // (Architect 2026-06-24: "Your bar on the left side is covering stuff in
  // the middle on mobile").  Re-check on mount because Playwright + some
  // browsers report the wrong innerWidth at the initial render tick.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768,
  );
  React.useEffect(() => {
    const sync = () => setIsMobile(window.innerWidth < 768);
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  const showBar = !isMobile || mobileOpen;

  return (
    <>
      {/* Mobile-only toggle pill — pinned bottom-left above the cockpit footer */}
      {isMobile && (
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="federation-faces-mobile-toggle"
          aria-label={mobileOpen ? 'Hide Federation faces' : 'Show Federation faces'}
          style={{
            position: 'fixed',
            left: '12px',
            bottom: 'calc(252px + 80px)',
            zIndex: 96,
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            border: '1px solid rgba(0, 255, 255, 0.4)',
            background: 'rgba(0, 0, 0, 0.8)',
            color: '#22d3ee',
            fontSize: '18px',
            cursor: 'pointer',
            boxShadow: '0 0 14px rgba(34,211,238,0.25)',
          }}
        >
          {mobileOpen ? '×' : '⌘'}
        </button>
      )}

      {showBar && (
        <aside
          data-testid="federation-faces-row"
          aria-label="NextXus Federation persona directory"
          style={{
            position: 'fixed',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '10px 8px',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 255, 255, 0.18)',
            borderRadius: '12px',
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }}
        >
          <div
            style={{
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: '8px',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#fbbf24',
              textAlign: 'center',
              paddingBottom: '4px',
              borderBottom: '1px solid rgba(251, 191, 36, 0.2)',
            }}
          >
            Federation
          </div>
          {FEDERATION_PERSONAS.map((p) => (
            <MonolithTile
              key={p.id}
              persona={p}
              onSelect={(id) => {
                setActivePersona(id);
                if (isMobile) setMobileOpen(false);
              }}
              dimmed={activePersona && activePersona !== p.id}
            />
          ))}
        </aside>
      )}

      <AIChatPopup
        personaId={activePersona}
        isOpen={Boolean(activePersona)}
        onClose={() => setActivePersona(null)}
      />
    </>
  );
}
